import React, { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ScrollToTop } from "./components/ScrollToTop";
import { HelmetProvider } from "react-helmet-async";
import TrackingScripts from "./components/TrackingScripts";
import DataLayerBridge from "./components/DataLayerBridge";
import StatusBanner from "./components/StatusBanner";
import { initUtmTracker } from "./lib/utmTracker";
import { AuthProvider } from "./hooks/useAuth";
import Index from "./pages/Index";
import ThemeBootstrap from "./components/ThemeBootstrap";
import CookieConsentBar from "./components/CookieConsentBar";

// Capture UTM params on first load
initUtmTracker();
import NotFound from "./pages/NotFound";
import BrandLoader from "./components/BrandLoader";

// Lazy-loaded routes for code splitting
const CoursesListPage = React.lazy(() => import("./pages/CoursesListPage"));
const CoursePage = React.lazy(() => import("./pages/CoursePage"));
const CourseSlugResolver = React.lazy(() => import("./pages/CourseSlugResolver"));
const EventsListPage = React.lazy(() => import("./pages/EventsListPage"));
const EventPage = React.lazy(() => import("./pages/EventPage"));
const EventThankYouPage = React.lazy(() => import("./pages/EventThankYouPage"));
const InstructorsListPage = React.lazy(() => import("./pages/InstructorsListPage"));
const InstructorPage = React.lazy(() => import("./pages/InstructorPage"));
const MaterialsListPage = React.lazy(() => import("./pages/MaterialsListPage"));
const MaterialPage = React.lazy(() => import("./pages/MaterialPage"));
const MaterialViewerPage = React.lazy(() => import("./pages/MaterialViewerPage"));
const MaterialOfferPage = React.lazy(() => import("./pages/MaterialOfferPage"));
const BlogListPage = React.lazy(() => import("./pages/BlogListPage"));
const BlogPostPage = React.lazy(() => import("./pages/BlogPostPage"));
const GrandeJuristaPage = React.lazy(() => import("./pages/GrandeJuristaPage"));
const GrandesJuristasListPage = React.lazy(() => import("./pages/GrandesJuristasListPage"));
const ArbitragemAplicadaThankYouPage = React.lazy(() => import("./pages/ArbitragemAplicadaThankYouPage"));
const PrivacyPolicyPage = React.lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfUsePage = React.lazy(() => import("./pages/TermsOfUsePage"));
const AdminLogin = React.lazy(() => import("./pages/admin/AdminLogin"));
const AdminForgotPassword = React.lazy(() => import("./pages/admin/AdminForgotPassword"));
const AdminResetPassword = React.lazy(() => import("./pages/admin/AdminResetPassword"));
const AdminLayout = React.lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = React.lazy(() => import("./pages/admin/AdminDashboard"));
const AdminCourses = React.lazy(() => import("./pages/admin/AdminCourses"));
const AdminCourseForm = React.lazy(() => import("./pages/admin/AdminCourseForm"));
const AdminEvents = React.lazy(() => import("./pages/admin/AdminEvents"));
const AdminInstructors = React.lazy(() => import("./pages/admin/AdminInstructors"));
const AdminInstructorForm = React.lazy(() => import("./pages/admin/AdminInstructorForm"));
const AdminGrandesJuristas = React.lazy(() => import("./pages/admin/AdminGrandesJuristas"));
const AdminPaginasVenda = React.lazy(() => import("./pages/admin/AdminPaginasVenda"));
const AdminPaginaVendaForm = React.lazy(() => import("./pages/admin/AdminPaginaVendaForm"));
const AdminGrandesJuristaForm = React.lazy(() => import("./pages/admin/AdminGrandesJuristaForm"));
const AdminMaterials = React.lazy(() => import("./pages/admin/AdminMaterials"));
const AdminMaterialForm = React.lazy(() => import("./pages/admin/AdminMaterialForm"));
const AdminFiles = React.lazy(() => import("./pages/admin/AdminFiles"));
const AdminPosts = React.lazy(() => import("./pages/admin/AdminPosts"));
const AdminPostForm = React.lazy(() => import("./pages/admin/AdminPostForm"));
const AdminCategories = React.lazy(() => import("./pages/admin/AdminCategories"));
const AdminEventForm = React.lazy(() => import("./pages/admin/AdminEventForm"));
const AdminB2bProducts = React.lazy(() => import("./pages/admin/AdminB2bProducts"));
const AdminB2bProductForm = React.lazy(() => import("./pages/admin/AdminB2bProductForm"));
const AdminB2bProposals = React.lazy(() => import("./pages/admin/AdminB2bProposals"));
const AdminB2bProposalForm = React.lazy(() => import("./pages/admin/AdminB2bProposalForm"));
const LinksPage = React.lazy(() => import("./pages/LinksPage"));
const B2BLandingPage = React.lazy(() => import("./pages/B2BLandingPage"));
const B2BProductPage = React.lazy(() => import("./pages/B2BProductPage"));
const B2bThankYouPage = React.lazy(() => import("./pages/B2bThankYouPage"));
const ProposalViewerPage = React.lazy(() => import("./pages/ProposalViewerPage"));
const AdminTracking = React.lazy(() => import("./pages/admin/AdminTracking"));
const AdminEngagement = React.lazy(() => import("./pages/admin/AdminEngagement"));
const AdminFunnel = React.lazy(() => import("./pages/admin/AdminFunnel"));

/**
 * URLs antigas do Arbitragem Aplicada convergem para a página oficial,
 * preservando query string (utm_*, src, sck, fbclid, gclid) e hash.
 */
const RedirectToArbitragem2 = () => {
  const location = useLocation();
  return <Navigate to={`/cursos/arbitragem-aplicada-2${location.search}${location.hash}`} replace />;
};

/** Redirect permanente preservando query string (utm_*, src, sck, fbclid, gclid) e hash. */
const RedirectTo = ({ to }: { to: string }) => {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: false,
      // Resilience for backend incidents (DNS failures, 5xx, timeouts):
      // retry up to 2 times with exponential backoff, fail fast after that
      // so the UI can render an error state with a manual retry button.
      retry: 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      networkMode: "offlineFirst",
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ThemeBootstrap />
        <AuthProvider>
        <ScrollToTop />
        <StatusBanner />
        <TrackingScripts />
        <DataLayerBridge />
        <Suspense fallback={<BrandLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/cursos" element={<CoursesListPage />} />
            {/* URLs antigas do Arbitragem Aplicada convergem para a página oficial (301 no _redirects) */}
            <Route path="/cursos/curso-tecnico-de-arbitragem" element={<RedirectToArbitragem2 />} />
            <Route path="/cursos/arbitragem-aplicada" element={<RedirectToArbitragem2 />} />
            <Route path="/cursos/:slug" element={<CourseSlugResolver />} />
            <Route path="/eventos" element={<EventsListPage />} />
            <Route path="/eventos/:slug" element={<EventPage />} />
            <Route path="/eventos/:slug/obrigado" element={<EventThankYouPage />} />
            <Route path="/formadores" element={<InstructorsListPage />} />
            <Route path="/formadores/:slug" element={<InstructorPage />} />
            <Route path="/materiais" element={<MaterialsListPage />} />
            {/* Slug antigo do e-book ECA Digital converge para a URL oficial */}
            <Route
              path="/materiais/novo-eca-digital-lei-15-211-2025-o-novo-padrao-regulatorio-que-esta-redefinindo-produtos-dados-e-estrategias-no-ambiente-digital"
              element={<RedirectTo to="/materiais/eca-digital-na-pratica" />}
            />
            <Route path="/materiais/:slug" element={<MaterialPage />} />
            <Route path="/materiais/:slug/oferta" element={<MaterialOfferPage />} />
            <Route path="/materiais/:slug/viewer" element={<MaterialViewerPage />} />
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/grandes-juristas/:slug" element={<GrandeJuristaPage />} />
            <Route path="/grandes-juristas" element={<GrandesJuristasListPage />} />
            <Route path="/obrigado-arbitragem-aplicada" element={<ArbitragemAplicadaThankYouPage />} />
            <Route path="/politica-de-privacidade" element={<PrivacyPolicyPage />} />
            <Route path="/termos-de-uso" element={<TermsOfUsePage />} />
            <Route path="/para-empresas" element={<RedirectTo to="/b2b" />} />
            <Route path="/links" element={<LinksPage />} />
            <Route path="/b2b" element={<B2BLandingPage />} />
            <Route path="/b2b/obrigado" element={<B2bThankYouPage />} />
            <Route path="/b2b/:slug" element={<B2BProductPage />} />
            <Route path="/b2b/:slug/obrigado" element={<B2bThankYouPage />} />
            <Route path="/propostas/:slug" element={<ProposalViewerPage />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="temas" element={<AdminCategories />} />
              <Route path="cursos" element={<AdminCourses />} />
              <Route path="cursos/novo" element={<AdminCourseForm />} />
              <Route path="cursos/:slug/editar" element={<AdminCourseForm />} />
              <Route path="eventos" element={<AdminEvents />} />
              <Route path="eventos/novo" element={<AdminEventForm />} />
              <Route path="eventos/:slug/editar" element={<AdminEventForm />} />
              <Route path="formadores" element={<AdminInstructors />} />
              <Route path="formadores/novo" element={<AdminInstructorForm />} />
              <Route path="formadores/:slug/editar" element={<AdminInstructorForm />} />
              <Route path="grandes-juristas" element={<AdminGrandesJuristas />} />
              <Route path="paginas-venda" element={<AdminPaginasVenda />} />
              <Route path="paginas-venda/nova" element={<AdminPaginaVendaForm />} />
              <Route path="paginas-venda/:id/editar" element={<AdminPaginaVendaForm />} />
              <Route path="grandes-juristas/novo" element={<AdminGrandesJuristaForm />} />
              <Route path="grandes-juristas/:id/editar" element={<AdminGrandesJuristaForm />} />
              <Route path="materiais" element={<AdminMaterials />} />
              <Route path="materiais/novo" element={<AdminMaterialForm />} />
              <Route path="materiais/:slug/editar" element={<AdminMaterialForm />} />
              <Route path="b2b/produtos" element={<AdminB2bProducts />} />
              <Route path="b2b/produtos/novo" element={<AdminB2bProductForm />} />
              <Route path="b2b/produtos/:slug/editar" element={<AdminB2bProductForm />} />
              <Route path="b2b/propostas" element={<AdminB2bProposals />} />
              <Route path="b2b/propostas/nova" element={<AdminB2bProposalForm />} />
              <Route path="b2b/propostas/:slug/editar" element={<AdminB2bProposalForm />} />
              <Route path="engajamento" element={<AdminEngagement />} />
              <Route path="funil" element={<AdminFunnel />} />
              <Route path="arquivos" element={<AdminFiles />} />
              <Route path="blog" element={<AdminPosts />} />
              <Route path="blog/novo" element={<AdminPostForm />} />
              <Route path="blog/:slug/editar" element={<AdminPostForm />} />
              <Route path="rastreamento" element={<AdminTracking />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <CookieConsentBar />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
